import { Separator } from "@/components/ui/baseUi/separator"
import { Text } from "@/components/ui/baseUi/text"
import { OfferAutonomousPurchase } from "@modules/boxOffer/entities/boxOfferTypes"
import { word } from "@modules/core/utils/i18n"
import { useEffect, useRef, useState } from "react"

export type HuAutonomousPurchaseFormProps = {
  setAutonomousPurchase: (b?: OfferAutonomousPurchase) => void
  autonomousPurchase: OfferAutonomousPurchase
} & HuAutonomousPurchaseProps

export type HuAutonomousPurchaseProps = {
  isPriceDisplay?: boolean
}

export function HuAutonomousPurchaseForm({
  setAutonomousPurchase,
  autonomousPurchase,
  isPriceDisplay = false,
}: HuAutonomousPurchaseFormProps) {
  const divRef = useRef(null)
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 })
  const [position, setPosition] = useState<number>(0)
  const [offerDisplay, setOfferDisplay] = useState({
    [OfferAutonomousPurchase.NO_ONE]: true,
    [OfferAutonomousPurchase.MEMBERS]: true,
    // [OfferAutonomousPurchase.LEADS_AND_DROP_IN]: true, // Disabling Leads and Drop In
    [OfferAutonomousPurchase.PUBLIC]: true,
  })
  const [isHandleClick, setIsHandleClick] = useState(true)

  const handleResize = () => {
    if (divRef.current) {
      const { width, height } = (divRef.current as HTMLElement).getBoundingClientRect()
      setDimensions({ width, height })
    }
  }

  const switchAutonomousPurchase = (offerAutonomousPurchase: OfferAutonomousPurchase, forceNoOne?: boolean) => {
    const newOfferDisplay = {
      [OfferAutonomousPurchase.NO_ONE]: forceNoOne ? false : true,
      [OfferAutonomousPurchase.MEMBERS]: false,
      // [OfferAutonomousPurchase.LEADS_AND_DROP_IN]: false, // Disabling Leads and Drop In
      [OfferAutonomousPurchase.PUBLIC]: false,
    }

    const optionsLength = Object.keys(newOfferDisplay).length

    if (offerAutonomousPurchase === OfferAutonomousPurchase.MEMBERS) {
      setPosition(Math.floor(dimensions.width / optionsLength / 2) + Math.floor(dimensions.width / optionsLength))
      newOfferDisplay[OfferAutonomousPurchase.MEMBERS] = true
      // Disabling Leads and Drop In
      // } else if (offerAutonomousPurchase === OfferAutonomousPurchase.LEADS_AND_DROP_IN) {
      //   setPosition(Math.floor(dimensions.width / optionsLength / 2) + Math.floor(dimensions.width / optionsLength) * 2)
      //   newOfferDisplay[OfferAutonomousPurchase.MEMBERS] = true
      //   newOfferDisplay[OfferAutonomousPurchase.LEADS_AND_DROP_IN] = true
    } else if (offerAutonomousPurchase === OfferAutonomousPurchase.PUBLIC) {
      // setPosition(Math.floor(dimensions.width / optionsLength / 2) + Math.floor(dimensions.width / optionsLength) * 3)// Disabling Leads and Drop In
      setPosition(Math.floor(dimensions.width / optionsLength / 2) + Math.floor(dimensions.width / optionsLength) * 2)
      newOfferDisplay[OfferAutonomousPurchase.MEMBERS] = true
      // newOfferDisplay[OfferAutonomousPurchase.LEADS_AND_DROP_IN] = true // Disabling Leads and Drop In
      newOfferDisplay[OfferAutonomousPurchase.PUBLIC] = true
    } else {
      setPosition(Math.floor(dimensions.width / optionsLength / 2))
    }

    setOfferDisplay(newOfferDisplay)
    setAutonomousPurchase(offerAutonomousPurchase)
  }

  const handleClick = (b: OfferAutonomousPurchase, forceNoOne?: boolean) => {
    if (!isHandleClick) return
    switchAutonomousPurchase(b, forceNoOne)
  }

  useEffect(() => {
    handleResize()
    window.addEventListener("resize", handleResize, false)
    if (!isPriceDisplay) switchAutonomousPurchase(OfferAutonomousPurchase.NO_ONE, true)
    else switchAutonomousPurchase(autonomousPurchase)
    return () => {
      window.removeEventListener("resize", handleResize, false)
    }
  }, [])

  useEffect(() => {
    if (!isPriceDisplay) switchAutonomousPurchase(OfferAutonomousPurchase.NO_ONE, true)
    else switchAutonomousPurchase(autonomousPurchase)
  }, [dimensions])

  useEffect(() => {
    if (!isPriceDisplay) {
      switchAutonomousPurchase(OfferAutonomousPurchase.NO_ONE, true)
      setIsHandleClick(false)
    } else {
      switchAutonomousPurchase(autonomousPurchase)
      setIsHandleClick(true)
    }
  }, [isPriceDisplay])

  return (
    <div
      className="flex flex-col gap-4 border border-neutral-800 bg-surface-foreground p-6 **:duration-400"
      style={{ borderRadius: "20px" }}
    >
      <Text fontStyle="BM" className="text-textDefault-body">
        {word("autonomousPurchase.form.title")}
      </Text>

      <Separator />

      {/* TITLE */}
      <div>
        <div className="flex flex-row gap-4">
          <Text
            fontStyle="TS"
            className={`transition-duration-200 flex-1 ${isHandleClick ? "cursor-pointer" : ""} ${offerDisplay[OfferAutonomousPurchase.NO_ONE] ? "text-textDefault-body" : "text-neutral-400"}`}
            onClick={() => handleClick(OfferAutonomousPurchase.NO_ONE)}
          >
            {word("autonomousPurchase.form.noOne.title")}
          </Text>
          <Text
            fontStyle="TS"
            className={`transition-duration-200 flex-1 ${isHandleClick ? "cursor-pointer" : ""} ${offerDisplay[OfferAutonomousPurchase.MEMBERS] ? "text-textDefault-body" : "text-neutral-400"}`}
            onClick={() => handleClick(OfferAutonomousPurchase.MEMBERS)}
          >
            {word("autonomousPurchase.form.members.title")}
          </Text>
          <Text
            fontStyle="TS"
            className={`transition-duration-200 flex-1 ${isHandleClick ? "cursor-pointer" : ""} ${offerDisplay[OfferAutonomousPurchase.PUBLIC] ? "text-textDefault-body" : "text-neutral-400"}`}
            onClick={() => handleClick(OfferAutonomousPurchase.PUBLIC)}
          >
            {word("autonomousPurchase.form.public.title")}
          </Text>
        </div>

        {/* DESCRIPTION */}
        <div className={`mt-2 flex flex-row gap-3`}>
          <Text
            fontStyle="BS"
            className={`transition-duration-200 flex-1 leading-normal ${isHandleClick ? "cursor-pointer" : ""} ${offerDisplay[OfferAutonomousPurchase.NO_ONE] ? "text-textDefault-body" : "text-neutral-400"}`}
            onClick={() => handleClick(OfferAutonomousPurchase.NO_ONE)}
          >
            {word("autonomousPurchase.form.noOne.subtitle")}
          </Text>
          <Text
            fontStyle="BS"
            className={`transition-duration-200 flex-1 leading-normal ${isHandleClick ? "cursor-pointer" : ""} ${offerDisplay[OfferAutonomousPurchase.MEMBERS] ? "text-textDefault-body" : "text-neutral-400"}`}
            onClick={() => handleClick(OfferAutonomousPurchase.MEMBERS)}
          >
            {word("autonomousPurchase.form.members.subtitle")}
          </Text>

          <Text
            fontStyle="BS"
            color={offerDisplay[OfferAutonomousPurchase.PUBLIC] ? "var(--textDefault-body)" : "var(--neutral-400)"}
            className={`transition-duration-200 flex-1 leading-normal ${isHandleClick ? "cursor-pointer" : ""} ${offerDisplay[OfferAutonomousPurchase.PUBLIC] ? "text-textDefault-body" : "text-neutral-400"}`}
            onClick={() => handleClick(OfferAutonomousPurchase.PUBLIC)}
          >
            {word("autonomousPurchase.form.public.subtitle")}
          </Text>
        </div>

        {/* COLORS */}
        <div ref={divRef} className={`relative mt-3 flex h-12 w-full flex-row items-center`}>
          <div
            className={`transition-duration-400 flex h-4 flex-1 rounded-l-lg ${
              (offerDisplay[OfferAutonomousPurchase.NO_ONE] ? "bg-primary-700" : "bg-neutral-600") +
              (isHandleClick ? " cursor-pointer" : "")
            }`}
            onClick={() => handleClick(OfferAutonomousPurchase.NO_ONE)}
          ></div>
          <div
            className={`transition-duration-400 flex h-4 flex-1 ${
              (offerDisplay[OfferAutonomousPurchase.MEMBERS] ? "bg-primary-600" : "bg-neutral-600") +
              (isHandleClick ? " cursor-pointer" : "")
            }`}
            onClick={() => handleClick(OfferAutonomousPurchase.MEMBERS)}
          ></div>
          {/* <div
          className={`flex flex-1 h-[1rem] transition-duration-400 ${
            (offerDisplay[OfferAutonomousPurchase.LEADS_AND_DROP_IN]
              ? "bg-primary-400"
              : "bg-neutral-600") + (isHandleClick ? " cursor-pointer" : "")
          }`}
          onClick={() => handleClick(OfferAutonomousPurchase.LEADS_AND_DROP_IN)}
        ></div> // Disabling Leads and Drop In */}
          <div
            className={`flex h-4 flex-1 rounded-r-lg ${
              (offerDisplay[OfferAutonomousPurchase.PUBLIC] ? "bg-primary-500" : "bg-neutral-600") +
              // (offerDisplay[OfferAutonomousPurchase.PUBLIC] ? "bg-primary-500" : "bg-neutralColor-700") + // Disabling Leads and Drop In
              (isHandleClick ? " cursor-pointer" : "")
            }`}
            onClick={() => handleClick(OfferAutonomousPurchase.PUBLIC)}
          ></div>

          {/* Position Indicator */}
          <div
            className="absolute h-full rounded-2xl bg-neutral-200"
            style={{ left: `${position.toString()}px`, width: "10px" }}
          ></div>
        </div>
      </div>
    </div>
  )
}
